import cn from "classnames";
import { IAssessment } from "@/modules/assessments/assessments.types";
import Overall from "@/modules/assessments/scenes/AssessmentReport/Overall/Overall";
import TemplateComposites from "@/modules/assessments/scenes/AssessmentReport/TemplateComposites/TemplateComposites";
import Interviews from "@/modules/assessments/scenes/AssessmentReport/Interviews/Interviews";
import { StatementsMap } from "@/modules/assessments/services/scales.service";
import { IClient, IUser } from "@/modules/client/clients.interface";
import { ReportContentView } from "@/shared/components/assessment/report/ReportComponent/ReportComponent";
import { IMultiChart, ITestAnswer } from "@/shared/components/charts/types/chartTypes";
import { IAssessmentInvite } from "@/shared/hooks/initializer/use-initialize-candidate.hook";
import { ReportExpirationBanner } from "@/shared/components/assessment/report/ReportExpirationBanner/ReportExpirationBanner";
import { getReportExpiration } from "@/shared/components/assessment/report/utils/getReportExpiration";
import { formatDate } from "@/shared/utils/formaters/dataFormater/formatDate";
import { IAssessmentComposite } from "@/modules/assessments/hooks/use-get-assessment-composites-by-invite.hook";
import PSPLogo from "@/assets/image/psp-logo.svg";
import style from "./style.module.css";
import MarkdownPreviewCustom from "@/ui-components/commonComponents/MarkdownPreviewCustom/MarkdownPreviewCustom";
import { IScaleAnalysis } from "@/modules/assessments/hooks/use-get-invite-scale-analyses.hook";
import { IDevelopmentSequence } from "@/modules/assessments/hooks/use-get-invite-scale-development.hook";
import Develop from "@/modules/assessments/scenes/AssessmentReport/Develop/Develop";
import DevelopIntlScope from "@/modules/assessments/scenes/AssessmentReport/Develop/DevelopIntlScope";
import { DEFAULT_LOCALE } from "@/i18n";
import { LanguagesIsoEnum } from "@/modules/assessments/types/test.interface";

interface INewReportToPrint {
  client?: Pick<IClient, "logoUrl" | "name">;
  candidate?: Pick<IUser, "fullName">;
  jobTitle?: string;
  contentView: ReportContentView;
  assessmentInvite?: Pick<IAssessmentInvite, "finishedOn" | "status">;
  assessment: Pick<IAssessment, "assessmentTests" | "name" | "resultValidityDays">;
  answers: ITestAnswer[];
  statements: StatementsMap;
  chartData: IMultiChart[];
  isScored: boolean;
  narrativeContent?: string | null;
  scaleAnalyses: IScaleAnalysis[];
  templateComposites?: IAssessmentComposite[];
  developmentSequences?: IDevelopmentSequence[];
  developmentLocales?: LanguagesIsoEnum[];
  developmentLocale?: LanguagesIsoEnum;
}

export function AssessmentTemplatePrint({
  candidate,
  client,
  jobTitle,
  contentView,
  assessmentInvite,
  assessment,
  answers,
  statements,
  chartData,
  narrativeContent,
  scaleAnalyses,
  templateComposites = [],
  developmentSequences,
  developmentLocales,
  developmentLocale,
}: INewReportToPrint) {
  const {
    isExpired,
    completedOn,
    resultValidityDays: validityDays,
  } = getReportExpiration({
    finishedOn: assessmentInvite?.finishedOn,
    resultValidityDays: assessment?.resultValidityDays,
  });

  const showExpirationBanner = isExpired && completedOn;

  return (
    <div
      className={cn(style.printReportContainer, style.assessmentTemplatePrint, {
        "is-expired-print": showExpirationBanner,
      })}
    >
      <table>
        <thead>
          <tr>
            <td className={style.reportHeaderCell}>
              <div className={style.reportHeader}>
                <div className={style.nameMatchRow}>
                  <p className={style.candidateName}>{candidate?.fullName}</p>
                </div>
              </div>
            </td>
          </tr>
        </thead>
        <tbody>
          {showExpirationBanner && (
            <tr className={style.expirationBannerRow}>
              <td>
                <ReportExpirationBanner completedOn={completedOn} resultValidityDays={validityDays} />
              </td>
            </tr>
          )}
          <tr>
            <td>
              <div className={style.reportContent}>
                <div className={style.assessmentDetails}>
                  <p className={style.assessmentName}>
                    <strong>{assessment.name[DEFAULT_LOCALE]}</strong>
                  </p>
                  <p className={style.jobDetail}>
                    <strong>Company Job Title:</strong> {jobTitle}
                  </p>
                </div>
                {contentView === ReportContentView.overall && (
                  <Overall
                    assessment={assessment}
                    answers={answers}
                    statements={statements}
                    chartData={chartData}
                    templateComposites={templateComposites}
                    showAssessmentTemplateExperience={true}
                    scaleAnalyses={scaleAnalyses}
                  />
                )}
                {contentView === ReportContentView.competencies && (
                  <TemplateComposites templateComposites={templateComposites} />
                )}
                {contentView === ReportContentView.interviews && <Interviews templateComposites={templateComposites} />}
                {contentView === ReportContentView.develop && developmentSequences && (
                  <DevelopIntlScope locale={developmentLocale ?? DEFAULT_LOCALE}>
                    <Develop
                      developmentSequences={developmentSequences}
                      availableLocales={developmentLocales}
                      locale={developmentLocale}
                    />
                  </DevelopIntlScope>
                )}
              </div>
            </td>
          </tr>
          {narrativeContent && (
            <tr>
              <td>
                <div>
                  <h2>Narrative</h2>
                  <MarkdownPreviewCustom source={narrativeContent} />
                </div>
              </td>
            </tr>
          )}
        </tbody>
        <tfoot>
          <tr>
            <td>
              <div className={style.emptyFooter}></div>
            </td>
          </tr>
        </tfoot>
      </table>

      <footer className={style.reportFooter}>
        <div className={style.footerWrapper}></div>
        <div className={style.footerRowContainer}>
          <img src={PSPLogo} alt="PSP Metrics" className={style.logoContainer} />
          <img src={client?.logoUrl} alt={`${client?.name}-logo`} className={style.logoContainer} />
        </div>
        <div className={style.footerRowContainer}>
          <p>Copyright &copy; {formatDate(new Date().toISOString(), "YYYY")} PSP Metrics</p>
          {assessmentInvite?.finishedOn && (
            <p>Finished On {formatDate(assessmentInvite.finishedOn, "MMMM Do, YYYY")}</p>
          )}
        </div>
      </footer>
    </div>
  );
}
